import { Button, Col, Layout, Modal, Row, Space, Typography } from 'antd';
import { useLoggedInUser } from '../../../hooks/useLoggedInUser';
import useQueryTeamRoles from '../../../hooks/useQueryTeamRoles';
import HeaderSection from '../../HeaderSection';
import PageLoadingSpinner from '../../PageLoadingSpinner';
import UpdateTeamRoleForm from './UpdateTeamRoleForm';
import RetiredTeamRoleForm from './RetiredTeamRoleForm';
import { teamRolesByStatus } from './util';
import type {
  StoredTeamRole,
  StoredUserRoleAssignment,
  UserRoleAssignmentCreateEvent,
  UserRoleAssignmentDeleteEvent,
} from '../../../hooks/types';
import { PlusOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import CreateTeamRoleForm from './CreateTeamRoleForm';
import { usePermission } from '../../../hooks/usePermission';
import { Optional } from '../../Shared';
import SearchUserSelect from '../../SearchUserSelect';
import useSaveUserRoleAssignments from '../../../hooks/useSaveUserRoleAssignments';
import { eventData } from '../../../lib/graphql/common/queries';
import { errorMessage, successMessage } from '../../../lib/utils/messages';
import useQueryTeamRoleAssignments, {
  useQueryTeamRoleAssignmentsQueryName,
} from '../../../hooks/useQueryTeamRoleAssignments';
import { recordStatusSchema } from '../types';
import { useNotify } from '../../../hooks/useNotify';
import { onApolloError } from '../../../lib/graphql/helpers';

const { Text } = Typography;

const SettingTitle = ({ title }: { readonly title: string }) => (
  <Row style={{ alignItems: 'center' }}>
    <Col span={24} style={{ textAlign: 'center' }}>
      <Text style={{ fontSize: '20px' }}>{title}</Text>
    </Col>
  </Row>
);

/**
 * @returns A list of forms for updating the roles in a team
 */
const TeamSettings = () => {
  const { notify } = useNotify();

  const { loggedInUser } = useLoggedInUser();

  const permission = usePermission(undefined, loggedInUser);

  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);

  const [selectedPrasAdminUserIds, setSelectedPrasAdminUserIds] = useState<
    readonly string[]
  >([]);

  const [saveUserRoleAssignments] = useSaveUserRoleAssignments(
    onApolloError(notify.error)
  );

  const { data: teamRoles } = useQueryTeamRoles(
    onApolloError(notify.error),
    undefined,
    true
  );

  const activeTeamRoles: readonly StoredTeamRole[] = teamRolesByStatus(
    teamRoles?.team_role,
    recordStatusSchema.Values.Active
  );

  const deletedTeamRoles: readonly StoredTeamRole[] = teamRolesByStatus(
    teamRoles?.team_role,
    recordStatusSchema.Values.Deleted
  );

  // FIXME: we cannot use any team filter to query related data like team_role_assignment since the column filter permission
  // is used in Hasura team metadata. This is a bug in Hasura, we should add teamType SOC filter back if the bug is fixed.
  // @see https://github.com/hasura/graphql-engine/issues/9283
  const {
    data: prasAdminTeamRoleAssignments,
    loading: loadingPrasAdminTeamRoleAssignment,
  } = useQueryTeamRoleAssignments({
    onError: onApolloError(notify.error),
    teamRoleName: 'PRAS Admin',
  });

  const assignedPrasAdminUserIds: readonly string[] = useMemo(
    () =>
      prasAdminTeamRoleAssignments?.team_role_assignment.flatMap(
        (teamRoleAssignment) =>
          teamRoleAssignment.user_role_assignments.map(
            (userRoleAssignment) => userRoleAssignment.user.id
          )
      ) ?? [],
    [prasAdminTeamRoleAssignments?.team_role_assignment]
  );

  const prasAdminTeamRoleAssignmentId =
    prasAdminTeamRoleAssignments?.team_role_assignment[0]?.id;

  useEffect(() => {
    setSelectedPrasAdminUserIds(assignedPrasAdminUserIds);
  }, [assignedPrasAdminUserIds]);

  if (loggedInUser === undefined || loadingPrasAdminTeamRoleAssignment) {
    return <PageLoadingSpinner />;
  }

  const onCreateNewRole = () => setShowCreateRoleModal(true);

  /**
   * Saves the PRAS Admins by creating or deleting user role assignment for SOC team and PRAS Admin role.
   */
  const onSavePrasAdmins = () => {
    if (prasAdminTeamRoleAssignmentId === undefined) {
      return notify.error(errorMessage.noPrasAdminRole);
    }

    const prasAdminUserIdsToCreate: readonly string[] =
      selectedPrasAdminUserIds.filter(
        (id) => !assignedPrasAdminUserIds.includes(id)
      );

    const prasAdminUserIdsToDelete: readonly string[] =
      assignedPrasAdminUserIds.filter(
        (id) => !selectedPrasAdminUserIds.includes(id)
      );

    const prasAdminsToDelete: readonly StoredUserRoleAssignment[] =
      prasAdminTeamRoleAssignments?.team_role_assignment
        .flatMap(
          (teamRoleAssignment) => teamRoleAssignment.user_role_assignments
        )
        .filter((userRoleAssignment) =>
          prasAdminUserIdsToDelete.includes(userRoleAssignment.user.id)
        ) ?? [];

    const userRoleAssignmentsToCreate: readonly UserRoleAssignmentCreateEvent[] =
      prasAdminUserIdsToCreate.map((userId) => ({
        user_id: userId,
        team_role_assignment_id: prasAdminTeamRoleAssignmentId,
        event: eventData('user_role_assignment_created'),
      }));

    const userRoleAssignmentsToDelete: readonly UserRoleAssignmentDeleteEvent[] =
      prasAdminsToDelete.map((userRoleAssignment) => ({
        user_role_assignment_id: userRoleAssignment.id,
        event: eventData('user_role_assignment_deleted'),
      }));

    void saveUserRoleAssignments({
      variables: {
        userRoleAssignmentsToCreate,
        userRoleAssignmentsToDelete,
      },
      onCompleted: () => {
        notify.success(successMessage.userRoleAssignmentSaved);
        void permission.reload();
      },
      refetchQueries: [useQueryTeamRoleAssignmentsQueryName],
    });
  };

  return (
    <Layout>
      <HeaderSection title="Settings" />

      <Optional show={permission.isPowerUser}>
        <SettingTitle title="Manage PRAS Admin" />
        <Space
          direction="vertical"
          style={{ margin: 'auto', minWidth: '500px', marginBottom: '20px' }}
        >
          <SearchUserSelect
            formName="selectPrasAdminForm"
            formListName="assignedPrasAdmins"
            fieldName="prasAdminUserIds"
            currentUserIds={assignedPrasAdminUserIds}
            onChange={(selectedUsers) =>
              setSelectedPrasAdminUserIds(selectedUsers.map(({ id }) => id))
            }
          />
          <Button
            id="save-pras-admin-btn"
            type="primary"
            onClick={onSavePrasAdmins}
            style={{ float: 'right' }}
          >
            Save PRAS Admins
          </Button>
        </Space>
      </Optional>

      <Optional show={permission.teamRoleManage}>
        <SettingTitle title="Active SOC Team Roles" />
        <Row justify="end">
          <Button
            type="primary"
            id="create-new-role-btn"
            onClick={onCreateNewRole}
            icon={<PlusOutlined />}
          >
            Create new role
          </Button>
        </Row>
        <Row
          id="soc-team-role-table"
          style={{
            maxHeight: '60vh',
            overflow: 'auto',
            border: '1px solid #cdcdcd',
            borderRadius: '10px',
            padding: '10px',
            marginTop: '20px',
            marginBottom: '40px',
          }}
        >
          <Col span={24} style={{ width: '100%' }}>
            {activeTeamRoles
              .filter((role) => role.role_group === 'SOC')
              .map((socRoles) => (
                <UpdateTeamRoleForm
                  key={socRoles.id}
                  role={socRoles}
                  showRoleGroup={false}
                />
              ))}
          </Col>
        </Row>
      </Optional>

      <SettingTitle title="Active Non-SOC Team Roles" />
      <Row
        id="non-soc-team-role-table"
        style={{
          maxHeight: '60vh',
          overflow: 'auto',
          border: '1px solid #cdcdcd',
          borderRadius: '10px',
          padding: '10px',
          marginTop: '20px',
          marginBottom: '40px',
        }}
      >
        <Col span={24} style={{ width: '100%' }}>
          {activeTeamRoles
            .filter((role) => role.role_group !== 'SOC')
            .map((socRoles) => (
              <UpdateTeamRoleForm
                key={socRoles.id}
                role={socRoles}
                showRoleGroup={true}
              />
            ))}
        </Col>
      </Row>

      <SettingTitle title="Retired Team Roles" />
      <Row
        id="retired-team-role-table"
        style={{
          maxHeight: '60vh',
          overflow: 'auto',
          border: '1px solid #cdcdcd',
          borderRadius: '10px',
          padding: '10px',
          marginTop: '20px',
          marginBottom: '40px',
        }}
      >
        <Col span={24} style={{ width: '100%' }}>
          {deletedTeamRoles.map((role) => (
            <RetiredTeamRoleForm key={role.id} role={role} />
          ))}
        </Col>
      </Row>

      <Modal
        title="New Role"
        open={showCreateRoleModal}
        footer={null}
        destroyOnClose
        onCancel={() => setShowCreateRoleModal(false)}
      >
        <CreateTeamRoleForm
          dismissModal={() => setShowCreateRoleModal(false)}
        />
      </Modal>
    </Layout>
  );
};

export default TeamSettings;
